import { useEffect, useRef, useState } from 'react';
import { MoreVertical, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  apiClient,
  type AdminTenantSummary,
  type ApiError,
} from '@/lib/api-client';

const STATUS_LABEL: Record<AdminTenantSummary['status'], string> = {
  active: 'Ativo',
  trial: 'Trial',
  suspended: 'Suspenso',
  canceled: 'Cancelado',
};

const STATUS_BADGE: Record<AdminTenantSummary['status'], string> = {
  active:
    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  trial:
    'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  suspended:
    'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  canceled:
    'bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
};

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return dateFormatter.format(d);
}

function getErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  const apiErr = err as ApiError | undefined;
  if (apiErr?.message) return apiErr.message;
  return 'Erro inesperado. Tente novamente.';
}

function LoadingSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
      <div className="divide-y divide-neutral-800">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3">
            <div className="h-4 w-40 animate-pulse rounded bg-neutral-800" />
            <div className="h-4 w-24 animate-pulse rounded bg-neutral-800" />
            <div className="h-4 w-16 animate-pulse rounded bg-neutral-800" />
            <div className="ml-auto h-4 w-20 animate-pulse rounded bg-neutral-800" />
          </div>
        ))}
      </div>
    </div>
  );
}

interface RowActionsMenuProps {
  tenant: AdminTenantSummary;
  isBusy: boolean;
  onDeactivate(): void;
  onActivate(): void;
  onDelete(): void;
}

function RowActionsMenu({
  tenant,
  isBusy,
  onDeactivate,
  onActivate,
  onDelete,
}: RowActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const removed = tenant.deletedAt !== null;
  if (removed) {
    return (
      <span className="text-xs text-neutral-500">Removido</span>
    );
  }

  const canDeactivate = tenant.status === 'active';
  const canActivate = tenant.status === 'suspended';

  return (
    <div ref={rootRef} className="relative inline-block text-left">
      <button
        type="button"
        aria-label="Ações"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={isBusy}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-44 overflow-hidden rounded-md border border-neutral-800 bg-neutral-900 shadow-lg"
        >
          {canDeactivate ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onDeactivate();
              }}
              className="block w-full px-3 py-2 text-left text-sm text-neutral-200 hover:bg-neutral-800"
            >
              Desativar
            </button>
          ) : null}
          {canActivate ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onActivate();
              }}
              className="block w-full px-3 py-2 text-left text-sm text-neutral-200 hover:bg-neutral-800"
            >
              Reativar
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
            className="block w-full px-3 py-2 text-left text-sm text-red-300 hover:bg-red-950/60"
          >
            Excluir
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function AdminClientesPage() {
  const queryClient = useQueryClient();
  const { data: tenants, isPending, isError, error } = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => apiClient.adminListTenants(),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
  };

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => apiClient.adminDeactivateTenant(id),
    onSuccess: invalidate,
    onError: (err) => {
      window.alert(getErrorMessage(err));
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => apiClient.adminActivateTenant(id),
    onSuccess: invalidate,
    onError: (err) => {
      window.alert(getErrorMessage(err));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.adminDeleteTenant(id),
    onSuccess: invalidate,
    onError: (err) => {
      window.alert(getErrorMessage(err));
    },
  });

  const isBusy =
    deactivateMutation.isPending ||
    activateMutation.isPending ||
    deleteMutation.isPending;

  function handleDeactivate(t: AdminTenantSummary) {
    if (!window.confirm(`Desativar o escritório "${t.name}"?`)) return;
    deactivateMutation.mutate(t.id);
  }

  function handleActivate(t: AdminTenantSummary) {
    if (!window.confirm(`Reativar o escritório "${t.name}"?`)) return;
    activateMutation.mutate(t.id);
  }

  function handleDelete(t: AdminTenantSummary) {
    if (
      !window.confirm(
        `Tem certeza que deseja excluir o escritório ${t.name}? Esta ação pode ser revertida apenas via banco de dados.`,
      )
    )
      return;
    deleteMutation.mutate(t.id);
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-100">
            Clientes
          </h1>
          <p className="mt-1 text-sm text-neutral-400">
            Gestão dos escritórios contratantes do VoxisAdv.
          </p>
        </div>
        <Link
          to="/admin/clientes/novo"
          className="inline-flex items-center gap-1.5 rounded-md bg-neutral-100 px-3 py-1.5 text-sm font-medium text-neutral-900 hover:bg-neutral-200"
        >
          <Plus className="h-4 w-4" />
          Novo cliente
        </Link>
      </header>

      {isPending ? (
        <LoadingSkeleton />
      ) : isError ? (
        <div className="rounded-lg border border-red-900/50 bg-red-950/30 p-4 text-sm text-red-200">
          Não foi possível carregar a lista de clientes agora.
          {error instanceof Error && error.message ? (
            <span className="ml-1 opacity-70">({error.message})</span>
          ) : null}
        </div>
      ) : !tenants || tenants.length === 0 ? (
        <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-8 text-center">
          <p className="text-sm text-neutral-400">
            Nenhum cliente cadastrado ainda. Comece adicionando o primeiro
            escritório.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
          <table className="w-full text-sm">
            <thead className="border-b border-neutral-800 bg-neutral-950/40 text-left text-xs font-medium uppercase tracking-wide text-neutral-400">
              <tr>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Membros</th>
                <th className="px-4 py-3">Criado em</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {tenants.map((t) => {
                const removed = t.deletedAt !== null;
                return (
                  <tr
                    key={t.id}
                    className={
                      removed
                        ? 'opacity-50 hover:bg-neutral-800/40'
                        : 'hover:bg-neutral-800/40'
                    }
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/clientes/${t.id}`}
                        className="font-medium text-neutral-100 hover:underline"
                      >
                        {t.name}
                      </Link>
                      {removed && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-neutral-800 px-2 py-0.5 text-xs font-medium text-neutral-300">
                          Removido
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-neutral-400">
                      {t.slug}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[t.status]}`}
                      >
                        {STATUS_LABEL[t.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-neutral-300">
                      {t.memberCount}
                    </td>
                    <td className="px-4 py-3 text-neutral-400">
                      {formatDate(t.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <RowActionsMenu
                        tenant={t}
                        isBusy={isBusy}
                        onDeactivate={() => handleDeactivate(t)}
                        onActivate={() => handleActivate(t)}
                        onDelete={() => handleDelete(t)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
